/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  IconButton,
  Badge,
  Text,
  VStack,
  HStack,
  Spinner,
  useColorModeValue,
  Button,
} from "@chakra-ui/react";
import { FiBell } from "react-icons/fi";
import api, { API_URL } from "utils/api";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/tr";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("tr");

/**
 * Genel Bildirimler Komponenti
 * Tüm bildirim tiplerini gösterir (SIM operasyonları, abonelik bildirimleri, vb.)
 * Server-Sent Events (SSE) ile real-time bildirim desteği sağlar
 */
export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  // İlk yükleme - mevcut bildirimleri getir (tüm tipler: sim_operation, abonelik, vb.)
  const fetchInitialNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/notifications/?unread_only=false&limit=50");
      const data = await res.json();
      if (res.ok) {
        setNotifications(data || []);
      }
    } catch (error) {
      // Hata sessizce yok sayılıyor
    } finally {
      setLoading(false);
    }
  }, []);

  // Server-Sent Events (SSE) ile real-time bildirimler
  useEffect(() => {
    // İlk bildirimleri yükle
    fetchInitialNotifications();

    // SSE bağlantısı kur (fetch ile cookie gönderimi için)
    // EventSource native cookie gönderemez, bu yüzden fetch ile SSE simüle ediyoruz
    const sseUrl = `${API_URL}/api/notifications/sse`;
    const abortControllerRef = { current: new AbortController() };
    const isConnectedRef = { current: true };
    const reconnectTimeoutRef = { current: null };
    
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    const connectSSE = async () => {
      // Önceki reconnect timeout'unu temizle
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      try {
        // Cookie otomatik gönderilir (credentials: "include" ile)
        const response = await fetch(sseUrl, {
          method: "GET",
          headers: {
            "Accept": "text/event-stream",
            "Cache-Control": "no-cache",
          },
          credentials: "include", // HttpOnly cookie otomatik gönderilir
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`SSE connection failed: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (isConnectedRef.current) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = line.slice(6); // "data: " kısmını kaldır
                if (data.trim()) {
                  const notification = JSON.parse(data);
                  
                  // Yeni bildirimi ekle (duplicate kontrolü)
                  setNotifications((prev) => {
                    const exists = prev.some((n) => n.id === notification.id);
                    if (exists) {
                      return prev;
                    }
                    // En yeni bildirimler başta
                    return [notification, ...prev].slice(0, 50);
                  });
                }
              } catch (error) {
                // SADECE development'ta console'a yaz
                if (isDevelopment) {
                  console.error("SSE notification parse error:", error);
                }
              }
            }
          }
        }
      } catch (error) {
        // AbortError beklenen bir durumdur (cleanup sırasında)
        if (error.name !== "AbortError" && isConnectedRef.current) {
          // SADECE development'ta console'a yaz
          if (isDevelopment) {
            console.error("SSE connection error:", error);
          }
          // 5 saniye sonra yeniden dene (sadece hala bağlıysak)
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isConnectedRef.current) {
              connectSSE();
            }
          }, 5000);
        }
      }
    };

    connectSSE();

    // Cleanup - Tüm kaynakları temizle
    return () => {
      isConnectedRef.current = false;
      
      // Reconnect timeout'unu temizle
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // SSE bağlantısını kes
      abortControllerRef.current.abort();
    };
  }, [fetchInitialNotifications]);

  // Tüm bildirimleri temizle
  const clearAllNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.delete("/api/notifications/");
      if (res.ok) {
        // Tüm bildirimleri temizle
        setNotifications([]);
      }
    } catch (error) {
      // SADECE development'ta console'a yaz
      // Bildirimler kritik olmadığı için sessizce yok sayılıyor
      if (process.env.NODE_ENV === 'development') {
        console.error("Bildirimler temizlenirken hata:", error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const getStatusColor = (statusCode, type) => {
    // Abonelik bildirimleri
    if (type === "abonelik" && statusCode === "abonelik_bitiyor") return "orange";
    // SIM operasyon bildirimleri
    if (statusCode === "number_gathered") return "green";
    if (statusCode === "inventory_missing" || statusCode === "invalid_identifier") return "red";
    if (statusCode === "timeout" || statusCode === "cancelled") return "orange";
    return "gray";
  };

  const getStatusIcon = (statusCode, type) => {
    // Abonelik bildirimleri
    if (type === "abonelik" && statusCode === "abonelik_bitiyor") return "⚠️";
    // SIM operasyon bildirimleri
    if (statusCode === "number_gathered") return "✅";
    if (statusCode === "inventory_missing" || statusCode === "invalid_identifier") return "❌";
    if (statusCode === "timeout" || statusCode === "cancelled") return "⏱️";
    return "ℹ️";
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    // Backend UTC olarak gönderiyor (timezone yoksa UTC kabul et).
    // Sabit format kullan: tıklama/süre sonrası değişen "3 saat sonra" gibi
    // göreli zaman yerine oluşturulma zamanı sabit kalacak.
    const iso = typeof timestamp === "string" && !timestamp.endsWith("Z") && !timestamp.includes("+")
      ? timestamp + "Z"
      : timestamp;
    const dt = dayjs.utc(iso).tz("Europe/Istanbul");
    return dt.isValid() ? dt.format("DD.MM.YYYY HH:mm") : "";
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      placement="bottom-end"
      isLazy
    >
      <PopoverTrigger>
        <Box position="relative">
          <IconButton
            aria-label="Bildirimler"
            icon={<FiBell />}
            variant="ghost"
            color="gray.700"
            fontSize="20px"
            _hover={{ 
              bg: "rgba(0, 0, 0, 0.05)",
              transform: "scale(1.1)"
            }}
            _active={{ transform: "scale(0.95)" }}
            transition="all 0.2s"
            size="md"
          />
          {notifications.length > 0 && (
            <Badge
              position="absolute"
              top="-2px"
              right="-2px"
              colorScheme="red"
              borderRadius="full"
              fontSize="10px"
              minW="18px"
              h="18px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              px={1}
            >
              {notifications.length > 99 ? "99+" : notifications.length}
            </Badge>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent w="400px" maxH="500px" _focus={{ boxShadow: "lg" }}>
        <PopoverBody p={0}>
          <Box p={3} borderBottom="1px solid" borderColor={borderColor}>
            <HStack justify="space-between" align="center">
              <Text fontWeight="bold" fontSize="md">
                Bildirimler
              </Text>
              {notifications.length > 0 && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={clearAllNotifications}
                  isLoading={loading}
                >
                  Temizle
                </Button>
              )}
            </HStack>
          </Box>
          <Box maxH="400px" overflowY="auto">
            {loading && notifications.length === 0 ? (
              <Box p={4} textAlign="center">
                <Spinner size="sm" />
              </Box>
            ) : notifications.length === 0 ? (
              <Box p={4} textAlign="center">
                <Text fontSize="sm" color="gray.500">
                  Bildirim yok
                </Text>
              </Box>
            ) : (
              <VStack spacing={0} align="stretch">
                {notifications.map((notif, index) => {
                  // API "extra_data", SSE "metadata" gönderiyor - ikisini de destekle
                  const metadata = notif.metadata || notif.extra_data || {};
                  const plaka = metadata.plaka || `Cihaz ${notif.device_id || "N/A"}`;
                  const sim_no = metadata.sim_no;
                  const imei = metadata.imei;
                  
                  return (
                    <Box
                      key={notif.id}
                      p={3}
                      borderBottom={index < notifications.length - 1 ? "1px solid" : "none"}
                      borderColor={borderColor}
                      _hover={{ bg: hoverBg }}
                    >
                      <HStack spacing={3} align="flex-start">
                        <Text fontSize="xl" lineHeight="1">
                          {getStatusIcon(notif.status_code, notif.type)}
                        </Text>
                        <Box flex={1} minW={0}>
                          <HStack spacing={2} mb={1} flexWrap="wrap">
                            <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                              {notif.title || plaka}
                          </Text>
                          <Badge
                            size="sm"
                            colorScheme={getStatusColor(notif.status_code, notif.type)}
                          >
                            {notif.type === "abonelik" && notif.status_code === "abonelik_bitiyor"
                              ? "Abonelik Bitiyor"
                              : notif.status_code === "number_gathered"
                              ? "Başarılı"
                              : notif.status_code === "inventory_missing"
                              ? "Envanterde Yok"
                              : notif.status_code === "invalid_identifier"
                              ? "Geçersiz"
                              : notif.status_code === "timeout"
                              ? "Zaman Aşımı"
                              : notif.status_code === "cancelled"
                              ? "İptal"
                              : notif.status_code || "Bildirim"}
                          </Badge>
                        </HStack>
                          {notif.message && (
                            <Text fontSize="xs" color="gray.600" mb={1} noOfLines={2}>
                              {notif.message}
                        </Text>
                          )}
                          <HStack spacing={2} flexWrap="wrap" fontSize="xs" color="gray.500">
                            {plaka && plaka !== `Cihaz ${notif.device_id || "N/A"}` && (
                              <Text>📋 {plaka}</Text>
                        )}
                            {sim_no && <Text>📱 {sim_no}</Text>}
                            {imei && <Text>🔢 {imei}</Text>}
                          </HStack>
                        <Text fontSize="xs" color="gray.400" mt={1}>
                            {formatTime(notif.created_at)}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                  );
                })}
              </VStack>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
